import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { isUUID } from 'class-validator';
import { useFlag } from '@loadsmart/vue-feature-flags';
import { useStore } from 'vuex';
import { get } from 'lodash';
import { useMixpanel } from '@/composables';
import { OBSERVATIONS_MOCK } from '../store';

export default function useGateFeed() {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const mixpanel = useMixpanel();

  const storePath = 'state.GateFeed';

  const MixPanelEvent = Object.freeze({
    VIEW_PAGE: 'Viewed: Page'
  });

  /*
   * Track mixpanel event for YMS module
   * @param {String} event - Mixpanel event name, check mixPanelEvent for available events
   * @param {Object} metadata - Additional metadata, as object
   * @returns {void}
   * */
  const trackMixpanelEvent = (event, { entryPoint, metadata }) => {
    try {
      const loggedUser = store.getters['Auth/me'];
      const eventData = {
        org_id: loggedUser?.orgId,
        org_name: loggedUser?.orgName,
        warehouse_id: selectedWarehouse.value?.id,
        warehouse_name: selectedWarehouse.value?.name,
        entry_point: entryPoint,
        // eslint-disable-next-line no-unused-vars
        ...(metadata && Object.fromEntries(Object.entries(metadata).filter(([_, v]) => v != null)))
      };

      mixpanel.track(
        event,
        // eslint-disable-next-line no-unused-vars
        Object.fromEntries(Object.entries(eventData).filter(([_, v]) => v != null))
      );
    } catch (err) {
      console.warn(`Failed to track mixpanel event ${event}`, err);
    }
  };

  // Read-only refs
  const enableGateFeedPage = useFlag('enable-gate-feed-page-helios');
  const enableGateFeedPageRef = computed(() => enableGateFeedPage.value.enabled);

  // Read-write refs
  const selectedWarehouse = computed({
    get() {
      return get(store, `${storePath}.selectedWarehouse`);
    },
    set(value) {
      store.commit('GateFeed/setSelectedWarehouse', value || {});

      if (value?.id) {
        // Make sure all related data is fetched
        fetchObservations();

        // Update warehouseId to the url
        router.push({
          params: { warehouseId: value.id }
        });
      } else {
        // Clean up the url
        router.push({
          name: 'gate-feed'
        });
      }
    }
  });
  const observations = computed(() => OBSERVATIONS_MOCK);

  // Route refs
  const defaultWarehouseId = computed(() =>
    isUUID(route.params?.warehouseId) ? String(route.params.warehouseId) : null
  );

  // Actions
  const fetchPreviousGateFeedData = () => store.dispatch('GateFeed/fetchPreviousGateFeedData');
  const persistSelectedWarehouse = warehouseId =>
    store.dispatch('GateFeed/persistSelectedWarehouse', warehouseId);
  const fetchObservations = () => store.dispatch('GateFeed/fetchObservations');
  const setIsLoadingObservations = value => store.commit('Yard/setIsLoadingObservations', value);

  return {
    // Read-Write Refs
    selectedWarehouseRef: selectedWarehouse,
    observationsRef: observations,
    // Read-Only Refs
    enableGateFeedPageRef,
    defaultWarehouseIdRef: defaultWarehouseId,
    // Methods
    fetchPreviousGateFeedData,
    trackMixpanelEvent,
    fetchObservations,
    setIsLoadingObservations,
    MixPanelEvent,
    // Basic Data
    persistSelectedWarehouse
  };
}
